import { useEffect, useMemo, useState } from 'react';
import type {
  Environment,
  EnvironmentDomain,
  ErpConnectionView,
  StoreInstallationView,
  StorePluginInstallResult,
  StoreSetup,
  StoreWebsiteView,
} from '../../api';
import { api, erpApi } from '../../api';
import { useResource } from '../../hooks';
import { ConfigurationDrawer } from '../project/ConfigurationDrawer';
import { LevelBadge, PanelCard, QuietEmpty, SectionHeader } from '../project/ui';
import { ActionMenu, Callout, Field, Select, Table } from '../primitives';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { ConfirmDialog, ErrorNotice, LoadingRows } from '../ui';
import { formatWhen, relativeWhen } from '../distribution/labels';
import {
  deliveredViaLabel,
  deliveryStateLabel,
  environmentTypeLabel,
  formatNumber,
  labelOr,
  priceUpdateModeLabel,
  storeSetupGaps,
  vatAnswerLabel,
  storeSeenLevel,
  websiteDeliveryState,
  websiteLabel,
  storePlatformLabel,
} from './labels';
import { usePublicDomains } from './usePublicDomains';
import { storeCopyFor } from './storeCopy';
import { StoreConnectGuide } from './StoreConnectGuide';
import { useAction } from './useAction';

/**
 * Stores: the Commerce Ops extension installed in each shop. A store knows
 * Commerce Ops' URL, its installation id and its key — one connection code
 * (CO-82) — and never anything about the ERP. Its own rules (the matching attribute, which ERP warehouse fills
 * which inventory source, VAT, overwrite or fill, creating products) are
 * made in the store (CO-79) and only shown here.
 *
 * One Magento can run several websites (CO-134). Each website takes stock
 * from its own ERP connection and has its own rules, so links and ERP facts
 * are per website; the connection code, updates and delivery are the
 * store's.
 *
 * On a WordPress site Commerce Ops reaches itself (hostedEnvironmentIds),
 * it installs the plugin and saves the code in WordPress in one step
 * (CO-137); a code to paste by hand stays available.
 */
export function ErpStoresPanel({
  projectId,
  projectName,
  environments,
  stores,
  connection,
  connectionNames = {},
  hostedEnvironmentIds = [],
  canManage,
  onChanged,
}: {
  projectId: string;
  /** Offered as a store name when the environment has no hostname yet. */
  projectName?: string;
  environments: Environment[];
  stores: StoreInstallationView[];
  connection: ErpConnectionView | null;
  /** The project's ERP connections by id, to name the one another website takes stock from. */
  connectionNames?: Record<string, string>;
  /** Environments whose WordPress site Commerce Ops reaches, so it can install the plugin there. */
  hostedEnvironmentIds?: string[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const action = useAction();
  const hosted = new Set(hostedEnvironmentIds);
  // Installing downloads, installs and waits for the plugin's first
  // check-in, so it can take a minute; the outcome is said either way.
  const install = (key: string, environmentId: string, storeName?: string) =>
    action.run(
      key,
      async () => {
        let result: StorePluginInstallResult;
        try {
          result = await erpApi.installStorePlugin(projectId, { environment_id: environmentId, name: storeName });
        } finally {
          onChanged();
        }
        if (!result.connected) {
          throw new Error(
            `The plugin ${result.version} is installed on ${result.installation.name}, but it has not checked in yet. ${result.message ?? ''}`.trim(),
          );
        }
        return result;
      },
      (result) => `Installed the plugin ${result.version} on ${result.installation.name} and connected it.`,
    );
  // What a website can take stock from: the project's connections, and at
  // least the one whose page this is.
  const names = connection ? { [connection.id]: connection.name, ...connectionNames } : connectionNames;
  const [environmentId, setEnvironmentId] = useState('');
  const [name, setName] = useState('');
  const [setup, setSetup] = useState<StoreSetup | null>(null);
  const [historyFor, setHistoryFor] = useState<StoreInstallationView | null>(null);
  const [removing, setRemoving] = useState<StoreInstallationView | null>(null);

  const taken = new Set(stores.map((store) => store.environment_id));
  const free = environments.filter((env) => !taken.has(env.id));
  const projectStores = stores.filter((store) => store.project_id === projectId);
  const publicDomains = usePublicDomains(projectStores);

  // The store's name is chosen, not typed: the hostnames the environment
  // already serves, then "project – environment" (the user's rule: a
  // dropdown wherever one is possible).
  const domains = useResource(
    () => (environmentId ? api.listEnvironmentDomains(environmentId) : Promise.resolve({ domains: [] as EnvironmentDomain[] })),
    [environmentId],
    environmentId, { key: `env:${environmentId}:domains` },
  );
  const chosenEnvironment = free.find((env) => env.id === environmentId);
  const chosenHosted = chosenEnvironment !== undefined && hosted.has(chosenEnvironment.id);
  // Nothing is offered until the environment's hostnames are known, so the
  // default is never a fallback chosen before they arrived.
  const nameOptions = useMemo(
    () => (domains.loading ? [] : storeNameOptions(domains.data?.domains ?? [], chosenEnvironment, projectName)),
    [domains.loading, domains.data, chosenEnvironment, projectName],
  );
  const [namePicked, setNamePicked] = useState(false);
  useEffect(() => {
    if (!namePicked || !nameOptions.includes(name)) setName(nameOptions[0] ?? '');
  }, [nameOptions, name, namePicked]);

  return (
    <>
      <PanelCard labelledBy="erp-stores-title" testId="erp-stores">
        <SectionHeader id="erp-stores-title" title="Stores" />
        <ErrorNotice error={action.error} />
        {action.notice ? (
          <p className="panel__note" role="status">
            {action.notice}
          </p>
        ) : null}
        {projectStores.length === 0 ? (
          <QuietEmpty>No store connected</QuietEmpty>
        ) : (
          <ul className="erp-store-list">
            {projectStores.map((store) => (
              <StoreRow
                key={store.id}
                store={store}
                connection={connection}
                connectionNames={names}
                canManage={canManage}
                hosted={hosted.has(store.environment_id)}
                busy={action.busy}
                domainOf={publicDomains.domainOf}
                onSetConnection={(website, connectionId) => {
                  const label = websiteLabel(store, website, projectStores.length > 1);
                  if (connectionId === '') {
                    if (!website.link_id) return;
                    void action.run(
                      `link:${store.id}:${website.code}`,
                      async () => {
                        await erpApi.unlinkStore(website.link_id as string);
                        onChanged();
                      },
                      `${label} no longer takes stock from the ERP. What it has stays as it is.`,
                    );
                    return;
                  }
                  void action.run(
                    `link:${store.id}:${website.code}`,
                    async () => {
                      // replace: a website that takes stock from another
                      // connection is moved in one step (CO-134).
                      await erpApi.linkStore(connectionId, store.id, website.code, true);
                      onChanged();
                    },
                    `${label} now takes stock and prices from ${names[connectionId] ?? 'the chosen connection'}.`,
                  );
                }}
                onResend={(website) =>
                  website.link_id &&
                  void action.run(
                    `resend:${store.id}:${website.code}`,
                    async () => {
                      const result = await erpApi.resend(website.link_id as string);
                      onChanged();
                      return result;
                    },
                    (result) =>
                      `Queued ${result.quantities_sent} stock values and ${result.prices_sent} prices for ${websiteLabel(store, website, projectStores.length > 1)}.`,
                  )
                }
                onRotate={() =>
                  void action.run(`rotate:${store.id}`, async () => {
                    const result = await erpApi.rotateStoreKey(store.id);
                    setSetup(result);
                    onChanged();
                    return result;
                  })
                }
                onToggle={() =>
                  void action.run(`toggle:${store.id}`, async () => {
                    await erpApi.setStoreEnabled(store.id, store.status !== 'active');
                    onChanged();
                  })
                }
                onInstall={() => void install(`install:${store.id}`, store.environment_id)}
                onHistory={() => setHistoryFor(store)}
                onRemove={() => setRemoving(store)}
              />
            ))}
          </ul>
        )}

        {setup ? (
          <StoreConnectGuide
            key={setup.installation.id + (setup.installation.key_rotated_at ?? '')}
            projectId={projectId}
            setup={setup}
            onConnected={onChanged}
            onClose={() => setSetup(null)}
          />
        ) : null}

        {canManage ? (
          free.length === 0 ? (
            projectStores.length === 0 ? (
              <QuietEmpty>Add an environment in Settings to connect a store to it.</QuietEmpty>
            ) : null
          ) : (
            <>
            <h3 id="erp-connect-store-title" className="erp-subheading">
              {projectStores.length === 0 ? 'Connect a store' : 'Connect another store'}
            </h3>
            <form
              className="erp-inline-form erp-inline-form--pair"
              aria-labelledby="erp-connect-store-title"
              onSubmit={(event) => {
                event.preventDefault();
                if (!environmentId || !name.trim()) return;
                if (chosenHosted) {
                  void install('install', environmentId, name.trim()).then((result) => {
                    if (!result) return;
                    setName('');
                    setNamePicked(false);
                    setEnvironmentId('');
                  });
                  return;
                }
                void action.run('create', async () => {
                  const result = await erpApi.createStore(projectId, { environment_id: environmentId, name: name.trim() });
                  setSetup(result);
                  setName('');
                  setNamePicked(false);
                  setEnvironmentId('');
                  onChanged();
                  return result;
                });
              }}
            >
              <Field label="Environment">
                <Select
                  id="erp-store-env"
                  value={environmentId}
                  onChange={(event) => {
                    setEnvironmentId(event.target.value);
                    setNamePicked(false);
                  }}
                >
                  <option value="">Choose an environment</option>
                  {free.map((env) => (
                    <option key={env.id} value={env.id}>
                      {environmentText(env.name, env.kind)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Store name">
                <Select
                  id="erp-store-name"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setNamePicked(true);
                  }}
                  disabled={nameOptions.length === 0}
                >
                  {nameOptions.length === 0 ? (
                    <option value="">{environmentId ? 'Reading the environment’s hostnames' : 'Choose the environment first'}</option>
                  ) : null}
                  {nameOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Field>
              <button
                type="submit"
                className="button button--primary"
                disabled={!environmentId || !name.trim() || action.busy !== null}
                data-testid="erp-connect-store"
              >
                {chosenHosted
                  ? action.busy === 'install'
                    ? 'Installing'
                    : 'Install and connect'
                  : action.busy === 'create'
                    ? 'Connecting'
                    : 'Connect'}
              </button>
            </form>
            {chosenHosted ? (
              <p className="field__help erp-connect-hosted" data-testid="erp-connect-hosted">
                Installing can take a minute.{' '}
                <button
                  type="button"
                  className="link-button"
                  disabled={action.busy !== null}
                  onClick={() =>
                    void action.run('create', async () => {
                      const result = await erpApi.createStore(projectId, { environment_id: environmentId, name: name.trim() });
                      setSetup(result);
                      setName('');
                      setNamePicked(false);
                      setEnvironmentId('');
                      onChanged();
                      return result;
                    })
                  }
                >
                  Get a code to paste instead
                </button>
              </p>
            ) : null}
            </>
          )
        ) : null}
      </PanelCard>

      {historyFor ? <DeliveryHistory store={historyFor} onClose={() => setHistoryFor(null)} /> : null}

      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removing?.name ?? 'this store'}?`}
        description="The store’s key stops working at once and Commerce Ops forgets its product list, matches and update history. Nothing in the store changes; uninstall the extension there if it is no longer needed."
        confirmLabel="Remove store"
        danger
        busy={action.busy === 'remove'}
        confirmText={removing?.name}
        onCancel={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          void action.run('remove', async () => {
            await erpApi.deleteStore(removing.id);
            setRemoving(null);
            onChanged();
          })
        }
      />
    </>
  );
}

function StoreRow({
  store,
  connection,
  connectionNames,
  canManage,
  hosted,
  busy,
  domainOf,
  onSetConnection,
  onResend,
  onRotate,
  onToggle,
  onInstall,
  onHistory,
  onRemove,
}: {
  store: StoreInstallationView;
  connection: ErpConnectionView | null;
  connectionNames: Record<string, string>;
  canManage: boolean;
  /** Commerce Ops reaches the store's WordPress site and can install the plugin there. */
  hosted: boolean;
  busy: string | null;
  /** The domain shoppers reach a website on, or null when not known (CO-158). */
  domainOf: (store: StoreInstallationView, website: StoreWebsiteView) => string | null;
  onSetConnection: (website: StoreWebsiteView, connectionId: string) => void;
  onResend: (website: StoreWebsiteView) => void;
  onRotate: () => void;
  onToggle: () => void;
  onInstall: () => void;
  onHistory: () => void;
  onRemove: () => void;
}) {
  const heard = Boolean(store.last_seen_at);
  const words = storeCopyFor(store.platform);
  const websites = store.websites ?? [];
  // Offered while the plugin is missing or still on a previous code.
  const installable = canManage && hosted && store.platform === 'woocommerce' && (!heard || store.previous_key_active);
  const [open, setOpen] = useState<string | null>(null);
  const titleId = `erp-store-${store.id}-title`;
  // The address the extension reported: on a headless store the shared
  // Magento API host, not a website's own domain (CO-158).
  const addressLabel = store.platform === 'woocommerce' ? 'Site address' : `${words.adminShort} API address`;

  const linkedName = (website: StoreWebsiteView) =>
    website.connection_id
      ? connectionNames[website.connection_id] ?? (website.connection_id === connection?.id ? connection.name : 'A connection of another project')
      : null;

  const columns: Column<StoreWebsiteView>[] = [
    {
      id: 'website',
      header: 'Website',
      card: 'title',
      width: 'fill',
      cell: (website) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{website.code === '' ? 'Whole store' : website.name || website.code}</span>
          {website.code !== '' ? (
            <span className="cell-stack__secondary">
              Code <code>{website.code}</code>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'domain',
      header: 'Public domain',
      width: 20,
      cell: (website) => {
        const domain = domainOf(store, website);
        return domain ? <span className="erp-domain">{domain}</span> : <span className="muted">Not known</span>;
      },
    },
    {
      id: 'source',
      header: 'ERP source',
      width: 16,
      cell: (website) => linkedName(website) ?? <span className="muted">None</span>,
    },
    {
      id: 'products',
      header: <span className="rt-num">Products</span>,
      cardLabel: 'Products',
      width: 10,
      priority: 'secondary',
      cell: (website) => <span className="rt-num tabular-nums">{heard && website.reported ? formatNumber(website.catalog_products) : '—'}</span>,
    },
    {
      id: 'delivery',
      header: 'Delivery status',
      width: 20,
      // A sentence, not a badge: "Waiting for the store: …" must wrap.
      cell: (website) => {
        const state =
          website.connection_id === undefined
            ? { level: 'unknown' as const, label: 'Nothing sent' }
            : websiteDeliveryState(store, website, words, website.connection_id === connection?.id ? connection : undefined);
        return (
          <span className="erp-state">
            <span className={`level-dot level-dot--${state.level}`} aria-hidden="true" />
            <span>{state.label}</span>
          </span>
        );
      },
    },
  ];

  return (
    <li className="erp-store" aria-labelledby={titleId} data-testid={`erp-store-${store.id}`}>
      <div className="erp-store__head">
        <div className="erp-store__title">
          <span className="erp-store__role">Store connector</span>
          <h3 id={titleId} className="erp-store__name">
            {store.name}
          </h3>
        </div>
        <div className="erp-store__badges">
          {store.status !== 'active' ? <LevelBadge level="unknown">Disabled</LevelBadge> : null}
          {!heard ? (
            <LevelBadge level="warning">Extension not heard from</LevelBadge>
          ) : (
            <LevelBadge level={storeSeenLevel(store.last_seen_at)}>
              {storeSeenLevel(store.last_seen_at) === 'ok' ? 'Seen' : 'Last seen'} {relativeWhen(store.last_seen_at)}
            </LevelBadge>
          )}
          {store.deliveries.dead > 0 ? <LevelBadge level="critical">Updates failing</LevelBadge> : null}
          {installable ? (
            <button
              type="button"
              className="button button--secondary button--small"
              disabled={busy !== null}
              onClick={onInstall}
              data-testid={`erp-store-install-${store.id}`}
            >
              {busy === `install:${store.id}` ? 'Installing' : 'Install and connect the plugin'}
            </button>
          ) : null}
          <ActionMenu
            label={`More actions for ${store.name}`}
            testId={`erp-store-menu-${store.id}`}
            items={[
              { label: 'Update history', onClick: onHistory },
              ...(canManage
                ? [
                    { label: busy === `rotate:${store.id}` ? 'Issuing' : 'Issue a new connection code', onClick: onRotate, disabled: busy !== null },
                    { label: store.status === 'active' ? 'Disable' : 'Enable', onClick: onToggle, disabled: busy !== null },
                    { label: 'Remove', onClick: onRemove, danger: true },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
      <dl className="erp-store__facts">
        <div>
          <dt>Environment</dt>
          <dd>{environmentText(store.environment_name, store.environment_type)}</dd>
        </div>
        <div>
          <dt>{addressLabel}</dt>
          <dd className="erp-domain">{store.reported_base_urls[0] ?? <span className="muted">Not reported yet</span>}</dd>
        </div>
        <div>
          <dt>Extension</dt>
          <dd>
            {store.module_version
              ? `${store.module_version} on ${labelOr(storePlatformLabel, store.platform)} ${store.platform_version}`
              : <span className="muted">Not reported yet</span>}
          </dd>
        </div>
        <div>
          <dt>Updates</dt>
          <dd>
            {store.latest_sequence === 0
              ? 'None sent yet'
              : `${formatNumber(store.latest_sequence)} sent, ${formatNumber(store.acknowledged_sequence)} fetched`}
            {store.deliveries.pending > 0 ? `, ${formatNumber(store.deliveries.pending)} waiting` : ''}
          </dd>
        </div>
      </dl>
      {!heard ? (
        installable ? null : (
          <Callout tone="warning" className="erp-store__waiting">
            Paste its connection code in {words.adminShort} ({words.connectionPath}) and click {words.saveButton}.
            {canManage ? ' Lost the code? Issue a new connection code from the menu.' : ''}
          </Callout>
        )
      ) : store.previous_key_active ? (
        <Callout tone="info" className="erp-store__waiting">
          The store uses its previous connection code until the new one is saved in {words.adminShort}
          {store.key_rotated_at ? ` (issued ${relativeWhen(store.key_rotated_at)})` : ''}.
        </Callout>
      ) : null}
      <ResponsiveTable
        rows={websites}
        rowKey={(website) => website.code || 'store'}
        caption={`Websites of ${store.name}`}
        className="erp-websites-table"
        columns={columns}
        actionsSize="md"
        actions={(website) => (
          <button
            type="button"
            className="button button--ghost button--small"
            aria-expanded={open === website.code}
            aria-controls={open === website.code ? `erp-website-details-${store.id}-${website.code || 'store'}` : undefined}
            aria-label={`${open === website.code ? 'Hide' : 'Show'} details of ${website.code === '' ? store.name : website.name || website.code}`}
            onClick={() => setOpen((current) => (current === website.code ? null : website.code))}
            data-testid={`erp-website-toggle-${store.id}-${website.code}`}
          >
            {open === website.code ? 'Hide details' : 'Details'}
          </button>
        )}
        detail={(website) =>
          open === website.code ? (
            <WebsiteDetails
              store={store}
              website={website}
              connection={connection}
              connectionNames={connectionNames}
              canManage={canManage}
              busy={busy}
              onSetConnection={(connectionId) => onSetConnection(website, connectionId)}
              onResend={() => onResend(website)}
            />
          ) : null
        }
        rowProps={(website) => ({ testId: `erp-website-${store.id}-${website.code}` })}
      />
    </li>
  );
}

/**
 * WebsiteDetails is one website of a store, opened from its row: which
 * connection it takes stock from, the ERP rules the store reported for it,
 * and its link actions.
 */
function WebsiteDetails({
  store,
  website,
  connection,
  connectionNames,
  canManage,
  busy,
  onSetConnection,
  onResend,
}: {
  store: StoreInstallationView;
  website: StoreWebsiteView;
  connection: ErpConnectionView | null;
  /** The project's ERP connections by id: what a website can take stock from. */
  connectionNames: Record<string, string>;
  canManage: boolean;
  busy: string | null;
  /** Moves, links or ('' ) unlinks the website. */
  onSetConnection: (connectionId: string) => void;
  onResend: () => void;
}) {
  const erp = website.erp;
  const words = storeCopyFor(store.platform);
  const heard = Boolean(store.last_seen_at);
  const bare = website.code === '';
  const linked = website.connection_id !== undefined;
  const linkedName = website.connection_id
    ? connectionNames[website.connection_id] ?? (website.connection_id === connection?.id ? connection.name : undefined)
    : undefined;
  const gaps = heard && website.reported ? storeSetupGaps(erp, words) : [];
  const locations = erp.locations ?? [];
  const busyKey = `${store.id}:${website.code}`;
  // A setting a website turned off is that website's, not the whole store's.
  const off = bare ? 'Off in the store' : 'Off for this website';
  // Each website chooses its own connection (CO-134): one dropdown moves,
  // links or unlinks it. A connection of another project is kept as an
  // option so the current choice always shows.
  const options = Object.entries(connectionNames).sort((a, b) => a[1].localeCompare(b[1]));
  if (website.connection_id && !connectionNames[website.connection_id]) {
    options.push([website.connection_id, linkedName ?? 'A connection of another project']);
  }
  const selectId = `erp-website-source-${store.id}-${website.code || 'store'}`;

  return (
    <div className="erp-website" id={`erp-website-details-${store.id}-${website.code || 'store'}`} data-testid={`erp-website-details-${store.id}-${website.code}`}>
      {website.reported && canManage && options.length > 0 ? (
        <div className="field erp-website__source">
          <label htmlFor={selectId}>Takes stock from</label>
          <Select id={selectId} value={website.connection_id ?? ''} disabled={busy !== null} onChange={(event) => onSetConnection(event.target.value)}>
            <option value="">Not taking stock from the ERP</option>
            {options.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      {linked && gaps.length > 0 ? (
        <Callout tone="warning" title={`Nothing is sent to ${bare ? 'this store' : 'this website'} until it is set up in the store`}>
          <ul className="erp-gap-list">
            {gaps.map((gap) => (
              <li key={gap}>{gap}.</li>
            ))}
          </ul>
          <p className="field__help">
            In {words.admin}: {words.settingsPath}
            {bare ? '' : `, with the ${website.name || website.code} website chosen as the scope`}.
          </p>
        </Callout>
      ) : null}
      {website.reported ? (
        <dl className="erp-website__facts">
          {website.base_urls[0] ? (
            <div>
              <dt>Base URL in the store</dt>
              <dd className="erp-domain">{website.base_urls[0]}</dd>
            </div>
          ) : null}
          <div>
            <dt>Match field</dt>
            <dd>
              {!heard ? '—' : erp.match_attribute ? <code>{erp.match_attribute}</code> : <span className="erp-missing">No {words.matchField} chosen</span>}
            </dd>
          </div>
          <div>
            <dt>Stock</dt>
            <dd>{heard ? (erp.enabled && erp.stock_sync ? 'Updated from the ERP' : off) : '—'}</dd>
          </div>
          <div>
            <dt>Prices</dt>
            <dd>
              {!heard
                ? '—'
                : !erp.enabled || !erp.price_sync
                  ? off
                  : erp.erp_prices_include_tax == null
                    ? <span className="erp-missing">{vatAnswerLabel(null)}</span>
                    : `${labelOr(priceUpdateModeLabel, erp.price_update_mode)}${erp.price_scope === 'website' && !bare ? ' for this website' : ''}`}
            </dd>
          </div>
          {heard && erp.enabled && erp.price_sync && erp.erp_prices_include_tax != null ? (
            <div>
              <dt>VAT</dt>
              <dd>
                ERP prices {erp.erp_prices_include_tax ? 'include' : 'exclude'} VAT; catalogue prices {erp.price_includes_tax ? 'include' : 'exclude'} it
              </dd>
            </div>
          ) : null}
          <div>
            <dt>ERP warehouses</dt>
            <dd>
              {!heard ? (
                '—'
              ) : locations.length === 0 ? (
                <span className="erp-missing">None mapped</span>
              ) : (
                <ul className="erp-location-list" aria-label={`ERP warehouse to ${words.stockLocation}`}>
                  {locations.map((row) => (
                    <li key={row.erp_warehouse}>
                      <code>{row.erp_warehouse}</code> → {row.location}
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
          <div>
            <dt>Product creation</dt>
            <dd>{heard ? (erp.enabled && erp.create_products ? `On: missing products are created ${words.createdAs}` : 'Off') : '—'}</dd>
          </div>
        </dl>
      ) : (
        <p className="panel__note">
          Nothing is sent to it. Stop taking stock from the ERP here if the website was removed in {words.adminShort}.
        </p>
      )}
      {canManage && linked ? (
        <div className="erp-actions">
          {website.reported ? (
            <button type="button" className="button button--secondary button--small" disabled={busy !== null} onClick={onResend}>
              {busy === `resend:${busyKey}` ? 'Queuing' : 'Send everything again'}
            </button>
          ) : (
            <button type="button" className="button button--ghost button--small" disabled={busy !== null} onClick={() => onSetConnection('')}>
              Stop taking stock from the ERP
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function DeliveryHistory({ store, onClose }: { store: StoreInstallationView; onClose: () => void }) {
  const deliveries = useResource(() => erpApi.deliveries(store.id, 50), [store.id], store.id);
  return (
    <ConfigurationDrawer
      open
      size="wide"
      title={`Updates sent to ${store.name}`}
      description="The store fetches any update it missed every minute, so a failed webhook delays an update rather than losing it."
      onClose={onClose}
    >
      <ErrorNotice error={deliveries.error} />
      {deliveries.loading && !deliveries.data ? (
        <LoadingRows label="Loading updates" rows={5} />
      ) : (deliveries.data?.deliveries ?? []).length === 0 ? (
        <QuietEmpty>
          {store.webhook_url
            ? 'No updates sent'
            : 'The store has no webhook; it fetches updates itself, so there is no webhook history.'}
        </QuietEmpty>
      ) : (
        <Table label={`Webhook deliveries to ${store.name}`} compact className="erp-table">
          <thead>
            <tr>
              <th scope="col">Update</th>
              <th scope="col">Kind</th>
              <th scope="col">State</th>
              <th scope="col">Attempts</th>
              <th scope="col">Last answer</th>
              <th scope="col">Queued</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.data?.deliveries.map((delivery) => (
              <tr key={delivery.id}>
                <th scope="row" className="tabular-nums">
                  {delivery.sequence}
                </th>
                <td>{delivery.event_type === 'price.updated' ? 'Prices' : 'Stock'}</td>
                <td>
                  {labelOr(deliveryStateLabel, delivery.state)}
                  {delivery.delivered_via ? ` ${deliveredViaLabel[delivery.delivered_via] ?? ''}` : ''}
                </td>
                <td className="tabular-nums">{delivery.attempts}</td>
                <td className="erp-table__note">
                  {delivery.last_error || (delivery.last_status ? `HTTP ${delivery.last_status}` : '—')}
                </td>
                <td>{formatWhen(delivery.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </ConfigurationDrawer>
  );
}

/** environmentText names an environment, adding its type only when the name does not already say it. */
function environmentText(name: string, type: string): string {
  const typeLabel = labelOr(environmentTypeLabel, type);
  if (!name) return typeLabel;
  return name.toLowerCase() === typeLabel.toLowerCase() ? name : `${name} (${typeLabel})`;
}

/**
 * storeNameOptions are the names a store can be given: the environment's
 * hostnames (the primary one first), then "project – environment". It is
 * pure.
 */
export function storeNameOptions(domains: EnvironmentDomain[], environment: Environment | undefined, projectName?: string): string[] {
  if (!environment) return [];
  const rank: Record<string, number> = { primary: 0, frontend: 1, alias: 2, api: 3, admin: 4, media: 5 };
  const hostnames = [...domains]
    .sort((a, b) => (rank[a.role] ?? 9) - (rank[b.role] ?? 9) || a.hostname.localeCompare(b.hostname))
    .map((d) => d.hostname.trim().toLowerCase())
    .filter((h) => h !== '');
  const fallback = `${projectName ? `${projectName} – ` : ''}${environment.name}`.slice(0, 120);
  return [...new Set([...hostnames.map((h) => h.slice(0, 120)), fallback])];
}
